import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Hash, Play, Loader2, ArrowLeft, Clapperboard, FileText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ReelGrid from "@/components/ReelGrid";
import { linkify } from "@/components/ExpandableText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TagPage() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    axios.get(`${API}/hashtags/${encodeURIComponent(tag)}/posts`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Hashtag não encontrada"));
  }, [tag]);

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="tag-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="tag-back"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
      <Card className="p-6 flex items-center gap-5 flex-wrap bg-gradient-to-br from-indigo-950 to-slate-800 text-white border-0">
        <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center shrink-0"><Hash className="w-10 h-10" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-white/60">Hashtag</div>
          <h1 className="text-3xl font-bold truncate" data-testid="tag-title">#{tag}</h1>
          <p className="text-white/70 text-xs mt-1" data-testid="tag-count">{data ? `${data.count} publicaç${data.count === 1 ? "ão" : "ões"} · ${data.reels.length} Reel${data.reels.length === 1 ? "" : "s"}` : ""}</p>
        </div>
        {data?.reels?.length > 0 && (
          <Button asChild className="rounded-full bg-white text-black hover:bg-white/90" data-testid="tag-watch-all">
            <Link to={`/reels?start=${data.reels[0].post_id}`}><Play className="w-4 h-4 mr-1" /> Assistir Reels</Link>
          </Button>
        )}
      </Card>

      {error && <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="tag-error">{error}</Card>}
      {!data && !error && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}
      {data && (
        <>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5"><Clapperboard className="w-4 h-4 text-pink-600" /> Reels em destaque</h2>
            {data.reels.length ? <ReelGrid reels={data.reels} testidPrefix="tag-reel" /> : <p className="text-xs text-muted-foreground" data-testid="tag-reels-empty">Nenhum Reel com esta hashtag ainda.</p>}
          </section>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5"><FileText className="w-4 h-4 text-blue-600" /> Publicações</h2>
            {data.posts.length ? (
              <div className="space-y-2" data-testid="tag-posts-list">
                {data.posts.map((p) => {
                  const img = p.image_urls?.[0] || p.image_url;
                  return (
                    <Card key={p.post_id} className="p-3 flex gap-3 hover:bg-muted/40 transition-colors" data-testid={`tag-post-${p.post_id}`}>
                      <Avatar className="w-9 h-9"><AvatarImage src={p.author_picture} /><AvatarFallback>{p.author_name?.[0]}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground"><Link to={`/user/${p.user_id}`} className="font-semibold text-foreground hover:underline">{p.author_name}</Link> · {new Date(p.created_at).toLocaleDateString("pt-BR")}</div>
                        <Link to={`/post/${p.post_id}`} className="block"><p className="text-sm line-clamp-3 whitespace-pre-wrap">{linkify(p.content || "")}</p></Link>
                        <div className="text-[11px] text-muted-foreground mt-1">{(p.likes || []).length} curtidas · {(p.comments || []).length} comentários</div>
                      </div>
                      {img && <Link to={`/post/${p.post_id}`}><img src={img} alt="" className="w-16 h-16 rounded-lg object-cover" /></Link>}
                    </Card>
                  );
                })}
              </div>
            ) : <p className="text-xs text-muted-foreground" data-testid="tag-posts-empty">Nenhuma publicação com esta hashtag.</p>}
          </section>
        </>
      )}
    </div>
  );
}
